// 权限判断辅助工具

// OIDC scopes requested at both authorize and token time. `offline_access` is
// REQUIRED for the IdP (e.g. Entra ID) to issue a refresh_token — without it no
// refresh_token is returned, so the app can never silently renew the session
// and every access-token expiry forces a re-login. Keep all authorize/grant
// call sites using this one constant so they can't drift apart.
export const OIDC_SCOPE = 'openid profile email offline_access';

export type GroupClaim = string | string[] | undefined;

/**
 * A glossary/reference collection or folder whose NAME contains "default"
 * (case-insensitive) is protected: only super-admins may create/edit/delete it
 * (or anything under a default-named folder); everyone else is read-only. Keep
 * in sync with the api-service isDefaultProtectedName().
 */
export const isDefaultProtectedName = (name?: string | null): boolean =>
  !!name && /default/i.test(name);

/**
 * 判断用户是否具备指定角色。
 * - 单权限时 `group` 为字符串，直接比较。
 * - 多权限时 `group` 为字符串数组，使用 includes。
 * - 兼容逗号分隔的字符串（做防御性处理）。
 */
export const hasRole = (group: GroupClaim, role: string): boolean => {
  if (!role) return false;
  if (group == null) return false;
  if (Array.isArray(group)) return group.includes(role);

  // 防御性处理：若是逗号分隔字符串，按数组处理
  const items = group.split(',').map((s) => s.trim()).filter(Boolean);
  if (items.length > 1) return items.includes(role);
  return group === role;
};

/**
 * 判断是否具备任意一个角色。
 */
export const hasAnyRole = (group: GroupClaim, roles: string[]): boolean => {
  if (!roles || roles.length === 0) return false;
  if (group == null) return false;
  if (Array.isArray(group)) return roles.some((r) => group.includes(r));

  const items = group.split(',').map((s) => s.trim()).filter(Boolean);
  if (items.length > 1) return roles.some((r) => items.includes(r));
  return roles.includes(group);
};

/**
 * 判断是否同时具备全部指定角色。
 */
export const hasAllRoles = (group: GroupClaim, roles: string[]): boolean => {
  if (!roles || roles.length === 0) return false;
  if (group == null) return false;
  if (Array.isArray(group)) return roles.every((r) => group.includes(r));

  const items = group.split(',').map((s) => s.trim()).filter(Boolean);
  if (items.length > 1) return roles.every((r) => items.includes(r));
  // 单权限时仅当所需角色列表只有一个并且匹配才返回 true
  return roles.length === 1 && roles[0] === group;
};

/**
 * 将 group 规范化为字符串数组，便于统一遍历。
 */
export const groupToArray = (group: GroupClaim): string[] => {
  if (group == null) return [];
  if (Array.isArray(group)) return group.filter((g) => typeof g === 'string');
  const items = group.split(',').map((s) => s.trim()).filter(Boolean);
  return items.length > 0 ? items : [group];
};

/**
 * 根据当前页面 URL 判断环境（dev / qa / 其他）。
 */
export const resolveEnv = (): 'dev' | 'qa' | 'prod' | 'other' => {
  try {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const h = (hostname || origin || '').toLowerCase();
    if (h.includes('.dev.')) return 'dev';
    if (h.includes('.qa.')) return 'qa';
    if (h.includes('.prd.')) return 'prod';
    return 'other';
  } catch {
    return 'other';
  }
};

// SuperAdmin: the highest tier. Has every Admin capability PLUS the ones
// removed from plain Admin (glossary/reference extraction and the dashboard).
export const superAdminRolesForEnv = (): string[] => {
  return ['4b28361f-a9a0-47ee-9582-bdac1984f4a3', 'BayLingoSuperAdmin'];
};

// Admin: manages glossary/reference collections. SuperAdmin is included here
// because it is a strict superset of Admin — every Admin-gated screen must also
// be reachable by SuperAdmin. Extraction and dashboard are NOT gated by this
// list anymore; they use superAdminRolesForEnv().
export const adminRolesForEnv = (): string[] => {
  return [
    '8a805462-3f0c-415d-bb25-0212c292fd9c',
    'BayLingoAdmin',
    ...superAdminRolesForEnv(),
  ];
};

export const userRolesForEnv = (): string[] => {
  return ['3e2b96df-3a25-4c69-ab5a-c90f999854c0', 'BayLingoGenericTeam'];
};

export const submissionRolesForEnv = (): string[] => {
  return ['d60ce263-a39e-4f23-bc67-bc1a671ad875', 'BayLingoSubmissionTeam'];
};

/**
 * 返回当前环境下允许访问的角色（用户 + 管理员 + 超级管理员 + submission）。
 * 注：adminRolesForEnv() 已包含 superAdmin，故此处无需再单独添加。
 */
export const allowedRolesForEnv = (): string[] => {
  return [...adminRolesForEnv(), ...userRolesForEnv(), ...submissionRolesForEnv()];
};

/**
 * 判断是否为 OIDC 环境（需要 group 校验）。
 * Cognito 环境不做 group 校验。
 */
export const isOidcEnv = (): boolean => {
  try {
    const appParam = JSON.parse(localStorage.getItem('INK_AI_APP_PARAM_20250331') || '{}');
    return !!appParam.cognito_host?.includes('microsoftonline');
  } catch {
    return false;
  }
};
